import type {
  PrivilegeCatalog,
  PrivilegeCatalogEntry,
  PrivilegeKey,
  Role,
} from '@/types/privileges.types';

/**
 * Default privilege bundles per role.
 *
 * The frontend uses these as a fallback when the backend privileges endpoint
 * is not available yet. Once the backend is live, `GET /me/privileges` is the
 * source of truth and these are only consulted to render the read-only
 * "defaults" column inside the super-admin Privilege Manager panel.
 */
export const ROLE_BUNDLES: Record<Role, PrivilegeKey[]> = {
  super_admin: [
    'admin.users.view',
    'admin.users.manage',
    'admin.roles.manage',
    'admin.privileges.manage',
    'admin.settings.manage',
    'subscriptions.view',
    'subscriptions.manage',
    'academy.subscriptions.view',
    'academy.subscriptions.manage',
    'academy.stats.view',
    'crm.contacts.view_all',
    'crm.contacts.manage',
    'crm.contacts.assign',
    'crm.activities.log',
    'crm.reports.view_own',
    'crm.reports.view_team',
    'curation.featured.manage',
    'rfq.disputes.manage',
    'rfq.designers.manage',
    'rfq.orders.manage',
    'ads.placements.manage',
    'discounts.send',
  ],
  admin: [
    'admin.users.view',
    'admin.settings.manage',
    'subscriptions.view',
    'academy.subscriptions.view',
    'academy.stats.view',
    'crm.contacts.view_all',
    'crm.contacts.manage',
    'crm.activities.log',
    'crm.reports.view_own',
    'curation.featured.manage',
    'rfq.disputes.manage',
    'rfq.designers.manage',
    'rfq.orders.manage',
    'ads.placements.manage',
    'discounts.send',
  ],
  sales_admin: [
    // Premium subscribers — the primary surface for sales
    'subscriptions.view',
    'subscriptions.manage',
    // Academy subscriptions — secondary surface
    'academy.subscriptions.view',
    'academy.subscriptions.manage',
    'academy.stats.view',
    // CRM — manage subscribers / pipeline / activities for renewals & signups
    'crm.contacts.view_assigned',
    'crm.contacts.view_all',
    'crm.contacts.manage',
    'crm.contacts.assign',
    'crm.activities.log',
    'crm.reports.view_own',
    // Outreach helpers
    'discounts.send',
  ],
  support_admin: [
    'admin.users.view',
    'subscriptions.view',
    'academy.subscriptions.view',
    'crm.contacts.view_all',
    'crm.activities.log',
    'rfq.disputes.manage',
  ],
  curation_admin: [
    'curation.featured.manage',
    'ads.placements.manage',
  ],
};

/** Built-in catalog used when backend `/admin/privileges/catalog` is unavailable. */
export const DEFAULT_PRIVILEGE_CATALOG: PrivilegeCatalog = {
  areas: [
    {
      area: 'admin',
      label: 'Administration',
      privileges: [
        { key: 'admin.users.view', label: 'View admin users', area: 'admin' },
        { key: 'admin.users.manage', label: 'Add / remove admin users', area: 'admin' },
        { key: 'admin.roles.manage', label: 'Assign roles to users', area: 'admin' },
        { key: 'admin.privileges.manage', label: 'Grant / revoke per-user privileges', area: 'admin' },
        { key: 'admin.settings.manage', label: 'Manage system settings', area: 'admin' },
      ],
    },
    {
      area: 'subscriptions',
      label: 'Premium Subscriptions',
      privileges: [
        { key: 'subscriptions.view', label: 'View premium subscribers', area: 'subscriptions' },
        { key: 'subscriptions.manage', label: 'Manage premium subscriptions (renewals, outreach)', area: 'subscriptions' },
      ],
    },
    {
      area: 'academy',
      label: 'Academy',
      privileges: [
        { key: 'academy.subscriptions.view', label: 'View academy subscriptions', area: 'academy' },
        { key: 'academy.subscriptions.manage', label: 'Create / upgrade academy subscriptions', area: 'academy' },
        { key: 'academy.stats.view', label: 'View academy statistics', area: 'academy' },
      ],
    },
    {
      area: 'crm',
      label: 'Sales CRM',
      privileges: [
        { key: 'crm.contacts.view_assigned', label: 'View subscribers assigned to me', area: 'crm' },
        { key: 'crm.contacts.view_all', label: 'View all subscribers', area: 'crm' },
        { key: 'crm.contacts.manage', label: 'Create / edit subscribers, move pipeline stage', area: 'crm' },
        { key: 'crm.contacts.assign', label: 'Assign / reassign subscribers to reps', area: 'crm' },
        { key: 'crm.activities.log', label: 'Log outreach, notes, reminders', area: 'crm' },
        { key: 'crm.reports.view_own', label: 'View own CRM reports', area: 'crm' },
        { key: 'crm.reports.view_team', label: 'View team-wide CRM reports', area: 'crm' },
      ],
    },
    {
      area: 'curation',
      label: 'Curation',
      privileges: [
        { key: 'curation.featured.manage', label: 'Manage featured designers / archives', area: 'curation' },
      ],
    },
    {
      area: 'marketplace',
      label: 'Marketplace',
      privileges: [
        { key: 'rfq.disputes.manage', label: 'Manage RFQ disputes', area: 'marketplace' },
        { key: 'rfq.designers.manage', label: 'Manage RFQ designer roster', area: 'marketplace' },
        { key: 'rfq.orders.manage', label: 'Manage RFQ orders & assignments', area: 'marketplace' },
      ],
    },
    {
      area: 'growth',
      label: 'Growth',
      privileges: [
        { key: 'ads.placements.manage', label: 'Manage ad placements', area: 'growth' },
        { key: 'discounts.send', label: 'Send discount codes', area: 'growth' },
      ],
    },
  ],
};

/**
 * Flatten role bundles into an effective privilege set.
 * Used as the frontend fallback when `GET /me/privileges` is unavailable.
 */
export const resolveBundlePrivileges = (roles: Role[]): PrivilegeKey[] => {
  const set = new Set<PrivilegeKey>();
  for (const role of roles) {
    const bundle = ROLE_BUNDLES[role] ?? [];
    for (const key of bundle) set.add(key);
  }
  return Array.from(set);
};

/**
 * Effective = (defaults ∪ grants) − revokes.
 * Mirrors the same calculation the backend MUST perform server-side.
 */
export const computeEffectivePrivileges = (
  defaults: PrivilegeKey[],
  grants: PrivilegeKey[],
  revokes: PrivilegeKey[],
): PrivilegeKey[] => {
  const set = new Set<PrivilegeKey>([...defaults, ...grants]);
  for (const k of revokes) set.delete(k);
  return Array.from(set);
};

/** Look up a single catalog entry. */
export const findCatalogEntry = (
  catalog: PrivilegeCatalog,
  key: PrivilegeKey,
): PrivilegeCatalogEntry | undefined => {
  for (const area of catalog.areas) {
    const found = area.privileges.find((p) => p.key === key);
    if (found) return found;
  }
  return undefined;
};
